import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Plus, 
  Search,
  MoreVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const Clients = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/crm/clients`);
      setClients(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/crm/clients`, {
        ...formData,
        tags: ["New"]
      });
      toast.success(isRTL ? "تم إضافة العميل" : "Client Added");
      setShowAdd(false);
      fetchClients();
    } catch (err) {
      toast.error(isRTL ? "فشل في إضافة العميل" : "Failed to add client");
    }
  };

  return (
    <div className="space-y-8">
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex flex-col gap-2 ${isRTL ? 'text-right' : ''}`}>
          <h1 className="text-4xl font-bold tracking-tight text-foreground font-heading">{t('pages.clients.title')}</h1>
          <p className="text-muted-foreground text-lg">
            {t('pages.clients.subtitle')}
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)} className={`gap-2 shadow-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Plus className="h-4 w-4" /> {t('pages.clients.addClient')}
        </Button>
      </div>

      <div className={`flex items-center gap-4 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4`} />
          <Input placeholder={t('pages.clients.searchPlaceholder')} className={isRTL ? 'pr-10 text-right' : 'pl-10'} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {clients.map((client) => (
          <Card key={client.id} className="group hover:border-primary/50 transition-colors">
            <CardContent className="p-6">
              <div className={`flex justify-between items-start mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="h-12 w-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Building2 className="h-6 w-6" />
                </div>
                <Badge variant="outline" className="capitalize">{client.type}</Badge>
              </div>
              
              <h3 className={`text-xl font-bold mb-1 ${isRTL ? 'text-right' : ''}`}>{client.name}</h3>
              <p className={`text-sm text-muted-foreground mb-4 ${isRTL ? 'text-right' : ''}`}>{client.industry || (isRTL ? 'القطاع الحكومي' : 'Government Sector')}</p>
              
              <div className="space-y-2 text-sm">
                {client.email && (
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Mail className="h-4 w-4" /> {client.email}
                  </div>
                )}
                {client.phone && (
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Phone className="h-4 w-4" /> {client.phone}
                  </div>
                )}
                {client.address && (
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <MapPin className="h-4 w-4" /> {client.address}
                  </div>
                )}
              </div>

              <div className={`mt-6 pt-4 border-t border-border flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200">{t('pages.clients.active')}</Badge>
                <Button variant="ghost" size="sm">{t('pages.common.details')}</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {clients.length === 0 && !loading && (
            <div className="col-span-full py-12 text-center border-2 border-dashed border-border rounded-xl">
                <Users className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-medium">{isRTL ? 'لا يوجد عملاء بعد' : 'No Clients Yet'}</h3>
                <p className="text-muted-foreground mb-4">{isRTL ? 'ابدأ بإضافة أول عميل أو حساب.' : 'Start by adding your first agency or account.'}</p>
                <Button variant="outline" onClick={() => setShowAdd(true)}>{t('pages.clients.addClient')}</Button>
            </div>
        )}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className={isRTL ? 'text-right' : ''}>
          <DialogHeader>
            <DialogTitle>{t('pages.clients.addClient')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('pages.clients.clientName')}</Label>
              <Input 
                onChange={e => setFormData({...formData, name: e.target.value})} 
                placeholder={isRTL ? 'مثال: وزارة النقل' : 'e.g. Dept of Transportation'} 
                className={isRTL ? 'text-right' : ''}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>{t('pages.clients.type')}</Label>
                    <select 
                        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${isRTL ? 'text-right' : ''}`}
                        onChange={e => setFormData({...formData, type: e.target.value})}
                    >
                        <option value="government">{isRTL ? 'حكومي' : 'Government'}</option>
                        <option value="enterprise">{isRTL ? 'مؤسسة' : 'Enterprise'}</option>
                        <option value="sme">{isRTL ? 'شركة صغيرة' : 'SME'}</option>
                    </select>
                </div>
                <div className="space-y-2">
                    <Label>{t('pages.clients.industry')}</Label>
                    <Input 
                      onChange={e => setFormData({...formData, industry: e.target.value})} 
                      placeholder={isRTL ? 'مثال: الدفاع' : 'e.g. Defense'} 
                      className={isRTL ? 'text-right' : ''}
                    />
                </div>
            </div>
            <div className="space-y-2">
              <Label>{t('pages.clients.email')}</Label>
              <Input 
                onChange={e => setFormData({...formData, email: e.target.value})} 
                className={isRTL ? 'text-right' : ''}
              />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowAdd(false)}>{t('pages.common.cancel')}</Button>
            <Button onClick={handleCreate}>{t('pages.common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
